import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { services as ALL_SERVICES } from '../mock/mockData';
import { SERVICES_KEYWORDS } from '../mock/serviceKeywords';
import { SURVEY_TYPES } from '../mock/surveyTypes';
import DotConnect from './DotConnect';
import ServiceBackdrop from './ServiceBackdrop';
import { useLang } from '../contexts/LanguageContext';

/**
 * PageHighlightStrip — sits above the global footer on every page.
 * Content (keywords, highlights, slogan) is unique per route.
 *
 * Lookup precedence:
 *   1. Exact static page map (home, services index, about, contact, etc.)
 *   2. /services/land-surveying/<sub>   → survey-sub-page derived content
 *   3. /services/<slug>                 → service-page derived content
 *   4. Generic fallback
 */

const STATIC = {
  '/': {
    eyebrow: 'Land Drafters · Global CAD Studio',
    title: 'One drafting partner. Every discipline. Every continent.',
    slogan: 'We Understand Your Unique Needs.',
    keywords: ['CAD Drafting', 'BIM', 'GIS', 'Survey CAD', 'MEP', 'Structural', 'Plant 3D', '24-48 hr TAT', 'ISO 9001:2015', 'NDA Protected'],
    highlights: [
      { icon: 'Layers',     label: '28 disciplines', sub: 'Survey · BIM · MEP · GIS · Plant' },
      { icon: 'Globe2',     label: '40+ countries',   sub: 'Five continents · 20 languages' },
      { icon: 'BadgeCheck', label: '350+ engineers',  sub: '15K+ projects delivered' }
    ],
    cta: { to: '/get-quote', label: 'Start a project' }
  },
  '/services': {
    eyebrow: 'All Services',
    title: 'Engineering & CAD drafting under one roof.',
    slogan: 'From concept to as-built — every discipline you need.',
    keywords: ['Land Surveying CAD Drafting', 'BIM', 'Civil 3D', 'Structural', 'MEP', 'Architectural', 'Plant Engineering', 'GIS', 'Manufacturing'],
    highlights: [
      { icon: 'Layers3',   label: '20+ services',    sub: '120+ specialised sub-services' },
      { icon: 'Timer',     label: '24-48 hr',         sub: 'Standard turnaround' },
      { icon: 'PiggyBank', label: 'Save up to 54%',   sub: 'vs in-house drafting cost' }
    ],
    cta: { to: '/get-quote', label: 'Request a quote' }
  },
  '/industries': {
    eyebrow: 'Industries We Serve',
    title: 'Domain depth across every sector we touch.',
    slogan: 'Speaking the codes, conventions, and software of your industry — fluently.',
    keywords: ['Smart Cities', 'Construction', 'Real Estate', 'Government', 'Energy', 'Industrial', 'Telecom', 'Transportation', 'Water', 'Renewable'],
    highlights: [
      { icon: 'Building',  label: '14 verticals',    sub: 'Built environment to renewables' },
      { icon: 'Award',     label: '20+ years',        sub: 'Code-compliant deliverables' },
      { icon: 'Users',     label: '350+ clients',     sub: 'Repeat & framework agreements' }
    ],
    cta: { to: '/contact', label: 'Talk to a sector expert' }
  },
  '/about': {
    eyebrow: 'About Land Drafters',
    title: 'A globally integrated CAD studio — built on precision and trust.',
    slogan: 'GROW TOGETHER — our philosophy since 2003.',
    keywords: ['Founded 2003', 'US + India ops', 'ISO 9001:2015', 'White-Label', 'NDA Protected', '20+ Languages'],
    highlights: [
      { icon: 'Sparkles',  label: '20+ years',        sub: 'Trusted multi-disciplinary studio' },
      { icon: 'Globe2',    label: '5 continents',     sub: '40+ countries served' },
      { icon: 'Heart',     label: 'Grow Together',    sub: 'Our customer-first philosophy' }
    ],
    cta: { to: '/about/team', label: 'Meet our team' }
  },
  '/about/team': {
    eyebrow: 'Our Team',
    title: 'Skilled engineers. Friendly drafters. One trusted partner.',
    slogan: 'Talent and passion — the engine behind every deliverable.',
    keywords: ['Leadership', 'Team Managers', 'Senior Engineers', 'Junior Engineers', 'HRA · Office', 'Marketing'],
    highlights: [
      { icon: 'Users',     label: '350+ people',     sub: 'Multi-disciplinary roster' },
      { icon: 'GraduationCap', label: 'PE / PLS',    sub: 'Licensed leadership' },
      { icon: 'Briefcase', label: 'Open roles',       sub: 'Always hiring great drafters' }
    ],
    cta: { to: '/careers', label: 'View open roles' }
  },
  '/about/vision': {
    eyebrow: 'Vision & Mission',
    title: 'What drives us — and where we are going.',
    slogan: 'Empowering businesses worldwide through talent and passion.',
    keywords: ['Vision', 'Mission', 'Philosophy', 'Grow Together', 'Engineering Excellence'],
    highlights: [
      { icon: 'Eye',     label: 'Vision',   sub: 'Empower businesses worldwide' },
      { icon: 'Target',  label: 'Mission',  sub: 'Enduring partnerships' },
      { icon: 'Heart',   label: 'Philosophy', sub: 'Grow Together' }
    ],
    cta: { to: '/why-us', label: 'Why choose us' }
  },
  '/why-us': {
    eyebrow: 'Why Choose Us',
    title: 'The capabilities that make us the preferred CAD partner.',
    slogan: 'Quality first. On-time. Accurate. Every project.',
    keywords: ['Survey-Grade Accuracy', '24-48 hr', 'PE / PLS Stamped', 'ISO 9001:2015', 'Secure Data', 'White-Label'],
    highlights: [
      { icon: 'ShieldCheck', label: 'ISO 9001:2015', sub: '3-stage QC process' },
      { icon: 'Lock',       label: 'NDA Protected',  sub: 'Enterprise-grade data security' },
      { icon: 'TrendingUp', label: 'Scale flexibly', sub: 'Dedicated or project teams' }
    ],
    cta: { to: '/get-quote', label: 'Start your project' }
  },
  '/coverage': {
    eyebrow: 'Global Coverage',
    title: '40+ countries. Five continents. One trusted CAD partner.',
    slogan: 'Region-aware contacts, local conventions, global delivery.',
    keywords: ['Americas', 'Europe', 'Asia-Pacific', 'Middle East', 'Oceania', 'US · India · UK · Canada · UAE'],
    highlights: [
      { icon: 'Globe2',    label: '40+ countries',    sub: 'Active project delivery' },
      { icon: 'Languages', label: '20 languages',     sub: 'RTL-ready multilingual SEO' },
      { icon: 'Clock',     label: '24/7 support',     sub: 'Time-zone-aligned teams' }
    ],
    cta: { to: '/contact', label: 'Find your nearest office' }
  },
  '/insights': {
    eyebrow: 'Insights & Knowledge',
    title: 'Engineering deep-dives, BIM workflows, and industry trends.',
    slogan: 'Stories and standards from our global drafting floor.',
    keywords: ['BIM Workflows', 'Scan-to-BIM', 'Digital Twin', 'GIS', 'Survey Tech', 'Manufacturing CAD'],
    highlights: [
      { icon: 'BookOpen',  label: 'Latest articles',  sub: 'Insights from our practice' },
      { icon: 'Newspaper', label: 'Industry news',    sub: 'Updates that matter' },
      { icon: 'Lightbulb', label: 'Best practices',   sub: 'Templates, tips, workflows' }
    ],
    cta: { to: '/insights', label: 'Browse all insights' }
  },
  '/blogs': {
    eyebrow: 'Engineering Blog',
    title: 'Deep dives by our drafting & BIM teams.',
    slogan: 'How we work, what we learn, and where engineering is going.',
    keywords: ['BIM', 'Civil 3D', 'AutoCAD', 'Revit', 'Tekla', 'GIS', 'Scan-to-BIM'],
    highlights: [
      { icon: 'Pencil',    label: 'Practical guides', sub: 'Workflows you can adopt today' },
      { icon: 'TrendingUp', label: 'Industry trends', sub: 'What clients are asking for' },
      { icon: 'Star',      label: 'Case studies',     sub: 'Real projects, real metrics' }
    ],
    cta: { to: '/blogs', label: 'Read the blog' }
  },
  '/news': {
    eyebrow: 'Land Drafters News',
    title: 'Announcements, milestones, and industry coverage.',
    slogan: 'Building publicly — milestones and partnerships.',
    keywords: ['Press', 'Awards', 'Partnerships', 'Milestones', 'Expansion'],
    highlights: [
      { icon: 'Megaphone', label: 'Announcements',    sub: 'Product, partnerships & wins' },
      { icon: 'Award',     label: 'Recognition',      sub: 'Industry honors & awards' },
      { icon: 'Globe2',    label: 'Global growth',    sub: 'New offices & coverage' }
    ],
    cta: { to: '/news', label: 'See all news' }
  },
  '/careers': {
    eyebrow: 'Careers at Land Drafters',
    title: 'Join a team that takes drafting seriously.',
    slogan: 'Global ambition. Local manner. Grow with us.',
    keywords: ['CAD Drafter', 'BIM Coordinator', 'Civil 3D Designer', 'Structural', 'GIS Analyst', 'Engineering'],
    highlights: [
      { icon: 'Rocket',    label: 'Grow your career', sub: 'Training & certifications' },
      { icon: 'Users',     label: 'Collaborative',    sub: '350+ engineers' },
      { icon: 'Globe',     label: 'Global exposure',  sub: 'Projects on 5 continents' }
    ],
    cta: { to: '/careers', label: 'View open roles' }
  },
  '/contact': {
    eyebrow: 'Contact Land Drafters',
    title: 'Tell us about your project — we typically respond in 24 hours.',
    slogan: 'From feasibility to final delivery — every step covered.',
    keywords: ['24/7 Support', 'NDA', 'White-Label', 'Region-Aware', 'Multi-language'],
    highlights: [
      { icon: 'Mail',      label: 'Email us',         sub: 'info@landdrafters.com · landdrafters@gmail.com' },
      { icon: 'Phone',     label: 'Call us',          sub: '(+1) 786-673-2523 · +91 9960 250 279' },
      { icon: 'Clock',     label: 'Quick response',   sub: 'Within 24 hours' }
    ],
    cta: { to: '/get-quote', label: 'Get a quote instead' }
  },
  '/get-quote': {
    eyebrow: 'Get a Free Quote',
    title: 'A detailed quote in 24 hours — no obligation, transparent pricing.',
    slogan: 'Tell us the scope. We listen, scope, and confirm.',
    keywords: ['Free Quote', '24 hr Response', 'No Obligation', 'NDA Available', 'Rush Available'],
    highlights: [
      { icon: 'FileText',  label: '4-step form',      sub: 'Scope, budget, delivery, contact' },
      { icon: 'Timer',     label: 'Fast quote',       sub: '24 hr standard, rush available' },
      { icon: 'ShieldCheck', label: 'Confidential',   sub: 'NDA before sharing details' }
    ],
    cta: { to: '/contact', label: 'Prefer a call?' }
  },
  '/privacy-policy': {
    eyebrow: 'Legal · Privacy',
    title: 'How we collect, use, and protect your information.',
    slogan: 'Privacy by design — across every region we serve.',
    keywords: ['Privacy', 'GDPR Aware', 'Data Security', 'ISO 9001:2015', 'NDA', 'Confidentiality'],
    highlights: [
      { icon: 'Lock',      label: 'Encrypted',        sub: 'In transit & at rest' },
      { icon: 'ShieldCheck', label: 'NDA Default',    sub: 'Standard on every project' },
      { icon: 'Eye',       label: 'Transparent',      sub: 'Clear data-use policy' }
    ],
    cta: { to: '/contact', label: 'Privacy questions?' }
  },
  '/terms': {
    eyebrow: 'Legal · Terms',
    title: 'The terms governing your use of our website and services.',
    slogan: 'Clear, fair, and aligned with industry norms.',
    keywords: ['Engagements', 'IP', 'Payment', 'Quality', 'Confidentiality', 'Liability'],
    highlights: [
      { icon: 'FileText',  label: '30-day quotes',    sub: 'Engagement on signed SOW' },
      { icon: 'Award',     label: 'Quality first',    sub: 'Unlimited revisions in scope' },
      { icon: 'Briefcase', label: 'IP transfers',     sub: 'On full payment' }
    ],
    cta: { to: '/contact', label: 'Terms questions?' }
  },
  '/services/land-surveying': {
    eyebrow: 'Land Survey CAD Drafting',
    title: 'CAD drafting for every survey type — we draft, you seal.',
    slogan: 'We do not perform field surveys. You collect — we draft.',
    keywords: ['ALTA / NSPS', 'Boundary', 'Topographic', 'As-Built', 'ROW', 'Subdivision', 'Tower', 'Pipeline', 'Tree Location', 'Mortgage Location'],
    highlights: [
      { icon: 'PenTool',   label: 'CAD-only',         sub: 'No field work on our side' },
      { icon: 'Stamp',     label: 'White-label',      sub: 'Sealed-ready for your firm' },
      { icon: 'Timer',     label: '24-48 hr TAT',     sub: 'Standard drafting turnaround' }
    ],
    cta: { to: '/services/land-surveying', label: 'Explore survey types' }
  }
};

const buildForServiceSlug = (slug) => {
  const svc = ALL_SERVICES.find(s => s.slug === slug);
  if (!svc) return null;
  const kw = SERVICES_KEYWORDS.find(k => k.slug === slug);
  return {
    eyebrow: svc.category,
    title: `${svc.title} — engineered for precision, delivered at scale.`,
    slogan: svc.short,
    keywords: kw ? kw.subServices.slice(0, 10) : [svc.title, svc.category, 'CAD Drafting', '24-48 hr TAT', 'White-Label'],
    highlights: [
      { icon: 'Layers',    label: kw ? `${kw.subServices.length} sub-services` : 'Specialised',
        sub: kw ? `Across ${svc.category.toLowerCase()}` : 'Tailored to your scope' },
      { icon: 'Timer',     label: '24-48 hr',          sub: 'Standard turnaround' },
      { icon: 'ShieldCheck', label: 'ISO 9001:2015',   sub: '3-stage quality check' }
    ],
    cta: { to: '/get-quote', label: `Quote for ${svc.title}` }
  };
};

const buildForSurveyType = (subSlug) => {
  const t = SURVEY_TYPES.find(x => x.slug === subSlug);
  if (!t) return null;
  return {
    eyebrow: 'Survey CAD Drafting',
    title: `${t.title} — from your field data, ready for your seal.`,
    slogan: t.short,
    keywords: t.deliverables.map(d => d.split(/ — | \(/)[0]).slice(0, 8),
    highlights: t.features.slice(0, 3).map(f => ({ icon: 'CheckCircle2', label: f.title, sub: f.desc })),
    cta: { to: '/get-quote', label: `Quote for ${t.title}` }
  };
};

const GENERIC = {
  eyebrow: 'Land Drafters',
  title: 'A globally integrated CAD studio at your service.',
  slogan: 'We Understand Your Unique Needs.',
  keywords: ['CAD Drafting', 'BIM', 'GIS', 'Survey CAD', '24-48 hr TAT', 'NDA Protected'],
  highlights: [
    { icon: 'Layers',     label: '28 disciplines',  sub: 'One studio · every CAD service' },
    { icon: 'Globe2',     label: '40+ countries',    sub: 'Five continents covered' },
    { icon: 'BadgeCheck', label: '350+ engineers',   sub: '15K+ projects shipped' }
  ],
  cta: { to: '/get-quote', label: 'Start a project' }
};

const resolveContent = (pathname) => {
  // Exact match
  if (STATIC[pathname]) return STATIC[pathname];
  // Survey sub-page
  const surveyMatch = pathname.match(/^\/services\/land-surveying\/([^/]+)\/?$/);
  if (surveyMatch) return buildForSurveyType(surveyMatch[1]) || STATIC['/services/land-surveying'];
  // Service detail
  const svcMatch = pathname.match(/^\/services\/([^/]+)\/?$/);
  if (svcMatch) return buildForServiceSlug(svcMatch[1]) || GENERIC;
  // About sub-pages (team detail etc.)
  if (pathname.startsWith('/about/team/')) return STATIC['/about/team'];
  // Insight detail
  if (pathname.startsWith('/insights/') || pathname.startsWith('/blogs/') || pathname.startsWith('/news/')) {
    return STATIC['/insights'];
  }
  return GENERIC;
};

const SKIP_PATHS = ['/admin'];

// Resolve a backdrop variant per route so the floating glyphs match the page's discipline.
const variantForPath = (pathname) => {
  const sub = pathname.match(/^\/services\/land-surveying\/([^/]+)\/?$/);
  if (sub) return { slug: sub[1] };
  const svc = pathname.match(/^\/services\/([^/]+)\/?$/);
  if (svc) return { slug: svc[1] };
  if (pathname.startsWith('/services/land-surveying')) return { variant: 'survey' };
  if (pathname.startsWith('/services'))                 return { variant: 'bim' };
  if (pathname.startsWith('/industries'))               return { variant: 'civil' };
  if (pathname.startsWith('/about') || pathname.startsWith('/why-us') || pathname.startsWith('/careers')) return { variant: 'bim' };
  if (pathname.startsWith('/coverage'))                 return { variant: 'gis' };
  if (pathname.startsWith('/insights') || pathname.startsWith('/blogs') || pathname.startsWith('/news')) return { variant: 'graphics' };
  if (pathname.startsWith('/contact') || pathname.startsWith('/get-quote')) return { variant: 'default' };
  return { variant: 'default' };
};

const PageHighlightStrip = () => {
  const { pathname } = useLocation();
  const { tt } = useLang();
  if (SKIP_PATHS.some(p => pathname.startsWith(p))) return null;
  const c = resolveContent(pathname);
  const bv = variantForPath(pathname);

  return (
    <section
      className="relative bg-gradient-to-br from-[#0e1116] via-gray-950 to-[#0e1116] text-white border-t border-gray-900/40 overflow-hidden"
      data-testid="page-highlight-strip"
    >
      <DotConnect density={75} color="#94a3b8" lineColor="rgba(148,163,184,0.85)" accentColor="#f97316" maxDistance={150} />
      <ServiceBackdrop slug={bv.slug} variant={bv.variant} accent="#94a3b8" accentAlt="#f97316" />
      <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>
      <div className="absolute -top-10 -right-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-gray-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative max-w-[1400px] mx-auto px-6 py-14 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold flex items-center gap-2 mb-3" data-testid="phs-eyebrow">
            <Icons.Sparkles className="w-3.5 h-3.5" /> {tt(c.eyebrow)}
          </div>
          <h2 className="text-3xl md:text-4xl font-black leading-tight" data-testid="phs-title">{tt(c.title)}</h2>
          <div className="mt-3 inline-flex items-center gap-2 text-green-300 italic text-sm" data-testid="phs-slogan">
            <span className="w-6 h-px bg-gray-400"></span> {tt(c.slogan)}
          </div>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="phs-keywords">
            {c.keywords.map(k => (
              <span key={k} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-gray-900/40 border border-gray-700/50 text-green-100 px-3 py-1.5 rounded-full">
                <span className="w-1 h-1 rounded-full bg-orange-400"></span> {tt(k)}
              </span>
            ))}
          </div>
          {c.cta && (
            <Link
              to={c.cta.to}
              className="inline-flex items-center gap-2 mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider transition-colors"
              data-testid="phs-cta"
            >
              {tt(c.cta.label)} <Icons.ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-3" data-testid="phs-highlights">
          {c.highlights.map(h => {
            const Ic = Icons[h.icon] || Icons.Sparkles;
            return (
              <div key={h.label} className="bg-[#1a1f2e]/80 backdrop-blur-sm border border-gray-700/40 rounded-lg p-5 hover:border-orange-500/70 hover:-translate-y-1 transition-all">
                <Ic className="w-6 h-6 text-orange-400" />
                <div className="font-black text-white mt-3 text-base leading-tight">{tt(h.label)}</div>
                <div className="text-[11px] text-green-300 mt-1 leading-snug">{tt(h.sub)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PageHighlightStrip;
